import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';

import { withNotification } from 'components/Notification';
import { NotificationTypes, apiErrorHandler } from 'utils';

import { SAVE_TRACKING_BILLING_SETTINGS, UPDATE_STORE } from 'gql/mutations';

import { useStore } from 'context/store-context';

import { CustomSubDomain } from './CustomSubDomain';
import { TrackingBillingSettings } from './TrackingBillingSettings';
import { TrackingPageEditor } from './TrackingPageEditor';
import {
  isAllOrdersTrackingEnabled,
  TRACKING_ALL_ORDERS_FEE_USD,
} from './trackingBillingFeatures';
import SettingsViewWrapper from 'components/SettingsWrapperView';

export const TrackingPage = withNotification(({ showNotification }: any) => {
  const store = useStore();
  const { storeId } = store;
  const [trackingBillingLoading, setTrackingBillingLoading] = useState(false);

  const [updateStore] = useMutation(UPDATE_STORE, {
    onError(error) {
      const newError = apiErrorHandler(error);
      showNotification(NotificationTypes.error, newError?.message);
    },
  });

  const [saveTrackingBillingSettings] = useMutation(
    SAVE_TRACKING_BILLING_SETTINGS,
    {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );

  const hasVisited =
    store.storeProperties?.completedSteps?.hasVisitedTrackingPage;

  useEffect(() => {
    if (store.storeProperties && !hasVisited) {
      updateStore({
        variables: {
          storeId,
          set: {
            completedSteps: {
              ...store.storeProperties.completedSteps,
              hasVisitedTrackingPage: true,
            },
          },
        },
      });
    }
    // The onboarding flag only needs writing once, when the merchant first
    // lands here. This effect previously had no dependency array and re-ran on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, hasVisited]);

  const handleTrackingBillingChange = async (enabled: boolean) => {
    if (!storeId || !store.storeProperties || trackingBillingLoading) return;

    try {
      setTrackingBillingLoading(true);
      await saveTrackingBillingSettings({
        variables: {
          input: {
            storeId,
            enabled,
          },
        },
      });
      showNotification(
        NotificationTypes.success,
        enabled
          ? `All-orders tracking enabled at $${TRACKING_ALL_ORDERS_FEE_USD.toFixed(
              2,
            )} per non-Guaranteed order.`
          : 'All-orders tracking disabled.',
      );
      await store.refetchStoreProperties?.();
    } catch {
      // Mutation errors are surfaced by saveTrackingBillingSettings; refetch errors do not undo the saved setting.
    } finally {
      setTrackingBillingLoading(false);
    }
  };

  return (
    <SettingsViewWrapper activeNav="tracking-templates">
      <div className="w-full h-full bg-[#f6f6f7]">
        <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
          <header className="max-w-2xl">
            <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
              Tracking
            </h1>
            <p className="text-[#6d7175] text-sm">
              Choose which orders ShipAid tracks, set your branded tracking
              address, and design the page your customers see.
            </p>
          </header>

          <TrackingBillingSettings
            enabled={isAllOrdersTrackingEnabled(store.storeProperties?.features)}
            loading={trackingBillingLoading}
            disabled={store.storeLoading || !store.storeProperties}
            onChange={handleTrackingBillingChange}
          />

          <CustomSubDomain />

          <TrackingPageEditor />
        </div>
      </div>
    </SettingsViewWrapper>
  );
});
